import { getInitialPageFromDOM } from '@inertiajs/core'
import { router } from '@inertiajs/react'
import posthog from 'posthog-js'

import type { AnalyticsSettings, SharedProps } from '@/types'

// Everything PostHog offers is on: autocapture with element text, session replay with
// input contents unmasked, heatmaps, dead clicks, rageclicks, console logs, web vitals,
// exception capture, surveys, and feature flags.
//
// Two things are deliberately not sent, and neither is a matter of taste:
//
// 1. Password fields. Kept masked in replay because a recording of somebody signing in
//    is their credential, not their behaviour. PostHog masks these by default; it is
//    written out here so turning masking off elsewhere cannot quietly take it with it.
// 2. Secrets that live in URLs. A password reset arrives as `?token=…` and an
//    invitation as `/invitation_activation?token=…`, both of which are live
//    credentials. `redactUrl` below strips them, and recording stops entirely on those
//    pages, because the replay's own metadata carries the address bar and `before_send`
//    cannot reach inside it. This mirrors TrackingScrubber, which does the same job for
//    the first-party events table.
const SECRET_PARAMETER = /token|secret|password|signature|key|code/i
// Long enough not to be a record id, opaque enough not to be a word. Matches
// TrackingScrubber::MIN_SECRET_LENGTH so a path reads the same in both stores.
const MIN_SECRET_LENGTH = 20
const REDACTED = '[redacted]'

let started = false

export function startAnalytics(settings: AnalyticsSettings) {
  // Inertia keeps one document alive for the whole session, and this module can be
  // evaluated twice during a development hot reload. A second init would attach a
  // second navigate listener and double every pageview.
  if (started) return
  started = true

  posthog.init(settings.api_key, {
    api_host: settings.host,
    defaults: '2026-05-30',

    // Anonymous visitors get a profile too, so the sign-in page and the invitation
    // funnel are measurable rather than beginning at the first identified event.
    person_profiles: 'always',

    autocapture: true,
    // Inertia navigates client-side: PostHog's own pageview capture would record the
    // first load and nothing after it. Fired by hand on every navigation instead.
    capture_pageview: false,
    capture_pageleave: true,
    capture_dead_clicks: true,
    capture_heatmaps: true,
    capture_performance: true,
    capture_exceptions: true,
    rageclick: true,
    enable_recording_console_log: true,

    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
      maskTextSelector: null,
      recordCrossOriginIframes: false,
    },

    before_send: (event) => {
      if (!event) return null

      for (const property of ['$current_url', '$referrer', '$pathname', 'url', 'page']) {
        const value = event.properties?.[property]
        if (typeof value === 'string') event.properties[property] = redactUrl(value)
      }

      return event
    },
  })
}

// Called on every Inertia navigation, so a sign-in, a sign-out, and an organization
// switch are all reflected without a full page load to notice them by.
export function syncIdentity(props: SharedProps) {
  if (!started) return

  const { current_user: user, current_organization: organization } = props

  if (!user) {
    // A signed-out browser must stop being the person who just left, or the next
    // student on a shared machine is merged into them.
    if (posthog.get_distinct_id() !== posthog.get_property('$device_id')) posthog.reset()
    return
  }

  posthog.identify(String(user.id), {
    email: user.email_address,
    name: user.full_name,
    timezone: user.timezone,
    platform_admin: user.platform_admin,
    roles: user.roles,
  })

  if (organization) {
    posthog.group('organization', String(organization.id), {
      name: organization.name,
      slug: organization.slug,
      status: organization.status,
    })
  }
}

export function capturePageview() {
  if (!started) return

  // Recording is suspended on the pages whose address is a credential. Resumed rather
  // than left off, so one invitation link does not cost the rest of the session.
  if (hasSecretParameter(window.location.search)) posthog.stopSessionRecording()
  else if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording()

  posthog.capture('$pageview', { $current_url: redactUrl(window.location.href) })
}

export function startPageviewTracking() {
  capturePageview()
  router.on('navigate', (event) => {
    // Identity is re-read on every navigation rather than once at boot, so signing in,
    // signing out, and switching organization are all noticed without a full page load
    // to notice them by.
    syncIdentity(event.detail.page.props as unknown as SharedProps)
    // Deferred a frame so document.title is the page being arrived at rather than the
    // one being left: Inertia swaps the component before the head is updated.
    requestAnimationFrame(capturePageview)
  })
}

// The props of the page the browser landed on. Inertia's own `navigate` event reports
// every visit after this one but not this one, so the first page is read from the DOM
// the server rendered it into.
export function initialSharedProps(): SharedProps | null {
  const page = getInitialPageFromDOM<{ props: SharedProps }>('app')
  return page?.props ?? null
}

function hasSecretParameter(search: string) {
  return [...new URLSearchParams(search).entries()].some(
    ([name, value]) => SECRET_PARAMETER.test(name) || looksSecret(value),
  )
}

// Keeps the route and the filters that make a URL worth reading — `?status=suspended`,
// `?query=…` — and removes the things that are credentials rather than navigation.
function redactUrl(value: string) {
  let url: URL
  try {
    url = new URL(value, window.location.origin)
  } catch {
    return value
  }

  for (const [name, parameter] of [...url.searchParams.entries()]) {
    if (SECRET_PARAMETER.test(name) || looksSecret(parameter)) url.searchParams.set(name, REDACTED)
  }

  url.pathname = url.pathname
    .split('/')
    .map((segment) => (looksSecret(segment) ? REDACTED : segment))
    .join('/')

  return url.toString()
}

// A record id stays; an opaque string of token length does not. Length and shape are
// what separate `/courses/12` from a 48-character device token in the same position.
function looksSecret(segment: string) {
  return segment.length >= MIN_SECRET_LENGTH && /^[A-Za-z0-9_-]+$/.test(segment) && /\d/.test(segment)
}
